import { useMemo } from 'react';
import { Event } from '../types/event.types';
import { formatDate, isToday, isTomorrow, isWeekend } from '../utils/dateUtils';

// ✅ OPTIMISATION ARRAY : Array vide stable pour éviter les re-renders
const EMPTY_EVENTS_ARRAY: Event[] = [];

interface FilterOptions {
  searchText: string;
  searchCity: string;
  selectedTags: string[];
  dateFilter: string[];
  timeFilter: string[];
  customDate?: string;
  dateRangeStart?: Date | null;
  dateRangeEnd?: Date | null;
  distance?: number;
  userLocation?: { latitude: number; longitude: number };
}

export const useEventFilters = (events: Event[] | undefined, options: FilterOptions) => {
  const {
    searchText,
    searchCity,
    selectedTags,
    dateFilter,
    timeFilter,
    customDate,
    dateRangeStart,
    dateRangeEnd,
    distance,
    userLocation
  } = options;

  return useMemo(() => {
    if (!events) return EMPTY_EVENTS_ARRAY;  // ✅ Array vide stable
    
    // ✅ OPTIMISATION : Vérifier si aucun filtre actif
    const hasFilters = searchText !== '' ||
                      searchCity !== '' ||
                      selectedTags.length > 0 ||
                      dateFilter.length > 0 ||
                      timeFilter.length > 0 ||
                      !!dateRangeStart ||
                      !!dateRangeEnd;

    // ✅ SÉCURITÉ : Toujours filtrer les événements expirés, même sans autres filtres
    const now = new Date();
    const filterExpiredEvents = (event: Event): boolean => {
      if (event.endTime) {
        const endTime = new Date(event.endTime);
        return endTime > now;
      } else if (event.datetime) {
        const eventDate = event.datetime instanceof Date ? event.datetime : new Date(event.datetime);
        const estimatedEndTime = new Date(eventDate.getTime() + (2 * 60 * 60 * 1000)); // +2h (raccord avec event.service.ts)
        return estimatedEndTime > now;
      }
      return true; // Garder si ni endTime ni datetime
    };

    if (!hasFilters) {
      return events.filter(filterExpiredEvents);
    }
    return events.filter(event => {
      // ✅ SÉCURITÉ : Filtrer les événements expirés (endTime ou datetime + 2h)
      const now = new Date();
      let isNotExpired = true;

      if (event.endTime) {
        // Si endTime existe, vérifier qu'il est dans le futur
        const endTime = new Date(event.endTime);
        isNotExpired = endTime > now;
      } else if (event.datetime) {
        // Si pas d'endTime mais datetime existe, vérifier datetime + 2h (raccord avec event.service.ts)
        const eventDate = event.datetime instanceof Date ? event.datetime : new Date(event.datetime);
        const estimatedEndTime = new Date(eventDate.getTime() + (2 * 60 * 60 * 1000)); // +2h
        isNotExpired = estimatedEndTime > now;
      }
      // Si ni endTime ni datetime, on garde l'événement par défaut (isNotExpired = true)

      const matchesSearch = searchText === '' ||
        event.title.toLowerCase().includes(searchText.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchText.toLowerCase());

      const matchesCity = searchCity === '' ||
        event.location?.toLowerCase().includes(searchCity.toLowerCase());
  
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.some(tag => {
          if (tag === 'favorites') {
            return localStorage.getItem(`favorite-${event.id}`) === 'true';
          }
          if (tag === 'free') {
            // No prices = unknown (scraped events), exclude from free filter
            if (!event.prices || event.prices.length === 0) return false;
            // All prices have amount === 0 = free
            return event.prices.every(p => p.amount === 0);
          }
          return event.tags?.includes(tag) || false;
        });

      const getEventDate = (event: Event): Date | null => {
        if (!event.datetime) return null;
        
        // ✅ Gérer les Timestamps Firestore
        let date: Date;
        if (event.datetime instanceof Date) {
          date = event.datetime;
        } else if (typeof event.datetime === 'object' && 'seconds' in event.datetime) {
          date = new Date((event.datetime as any).seconds * 1000);
        } else {
          date = new Date(event.datetime);
        }
        
        return isNaN(date.getTime()) ? null : date;
      };

      let matchesDate = true;
      const eventDate = getEventDate(event);
      if (dateFilter.length > 0 && eventDate) {
        matchesDate = dateFilter.some(filter => {
          if (filter.startsWith('custom_') || filter.startsWith('day_')) {
            const dateStr = filter.substring(filter.indexOf('_') + 1);
            return formatDate(eventDate) === dateStr;
          }
          
          switch (filter) {
            case 'today':
              return isToday(eventDate);
            case 'tomorrow':
              return isTomorrow(eventDate);
            case 'weekend':
              return isWeekend(eventDate);
            default:
              return false;
          }
        });
      }
  
      // Date range filter
      let matchesDateRange = true;
      if ((dateRangeStart || dateRangeEnd) && eventDate) {
        const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        if (dateRangeStart) {
          const startDay = new Date(dateRangeStart.getFullYear(), dateRangeStart.getMonth(), dateRangeStart.getDate());
          if (eventDay < startDay) matchesDateRange = false;
        }
        if (dateRangeEnd) {
          const endDay = new Date(dateRangeEnd.getFullYear(), dateRangeEnd.getMonth(), dateRangeEnd.getDate());
          if (eventDay > endDay) matchesDateRange = false;
        }
      }

      let matchesTime = true;
      if (timeFilter.length > 0 && eventDate) {
        const eventHour = eventDate.getHours();
        matchesTime = timeFilter.some(filter => {
          switch (filter) {
            case 'morning':
              return eventHour >= 6 && eventHour < 12;
            case 'afternoon':
              return eventHour >= 12 && eventHour < 18;
            case 'evening':
              return eventHour >= 18 || eventHour < 6;
            default:
              return false;
          }
        });
      }

      // 🎯 PRD HARMONISATION : Le filtre distance est maintenant géré par sortEvents
      return isNotExpired && matchesSearch && matchesCity && matchesTags && matchesDate && matchesDateRange && matchesTime;
    });
  }, [events, searchText, searchCity, selectedTags, dateFilter, timeFilter, customDate, dateRangeStart, dateRangeEnd, distance, userLocation]);
};

// 🎯 PRD HARMONISATION : calculateDistance supprimée - le filtre distance est géré par sortEvents